import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import LandingLayout from "./landing/LandingLayout";
import LandingForm from "./landing/LandingForm";
import QuizForm from "./landing/QuizForm";
import FocusedFunnelForm from "./landing/FocusedFunnelForm";
import CaseFunnelView from "./landing/caseFunnel/CaseFunnelView";
import QuizV2Page from "./landing/quizV2/QuizV2Page";
import CustomTrackingLoader from "./landing/CustomTrackingLoader";
import { useLandingSession } from "@/lib/useLandingSession";
import { ClassicTestimonials } from "./landing/BuyerTestimonials";
import { SharedCaseResults, SharedTrustBar, useFinalCta } from "./landing/SharedSections";
import { injectAllPixels } from "@/lib/pixelTracking";
import { pixelCheckNonce, reportPixelCheck } from "@/lib/pixelCheck";
import { useLandingSeo } from "@/lib/landingSeo";
import { emitPreviewEngagement } from "@/lib/prospectEngagement";
import { useLiveBuyer, shownDetails } from "./landing/useLiveBuyer";
import { hidesPhone } from "@/lib/phoneDisplay";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

/**
 * Iter-43: Public renderer for admin-authored custom buyer landing pages.
 *
 * Fetches `GET /api/public/custom-landing/{slug}` and mounts the same
 * LandingLayout + LandingForm the hardcoded verticals use, so form
 * validation + TrustedForm capture behave identically. When the URL carries
 * `?preview=1`, drafts + paused pages render for admin previews.
 *
 * If the slug maps to a hardcoded vertical (auto-accident, etc.), that
 * route wins in App.js — this component only ever sees custom pages.
 */
export default function CustomLandingPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  // Iter-50: client-share preview token — lets a buyer view drafts/paused
  // pages without logging in. Suppresses the admin ribbon, blocks form
  // submissions, and never fires marketing pixels.
  const demoToken = params.get("demo") || "";
  const brand = params.get("brand") || null;
  // Global funnels: `?b=<buyer-slug>` brands the shared page for that buyer.
  const buyerParam = params.get("b") || "";
  const affParam = params.get("aff") || "";
  const navigate = useNavigate();
  // Any preview surface (admin ?preview=1 OR shared demo token) counts as
  // "not real traffic" — used to gate pixel firing + form submissions.
  const isPreviewSurface = preview || Boolean(demoToken);
  const pixelCheckSent = useRef(false);

  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter-52: A/B variant assignment — sticky per anonymous session id.
  // Defaults to "standard" so existing pages behave EXACTLY like today
  // until the admin explicitly opts into Quiz or turns on an experiment.
  const [assignment, setAssignment] = useState(null);
  const previewVariant = params.get("variant");   // admin preview override
  // Iter-57: Focused Funnel mode-latch — flips to TRUE once the visitor
  // advances past Q1 so we hide marketing sections below the hero.
  // Only used when the resolved variant is `focused_funnel`. Standard
  // and Quiz surfaces never touch this flag.
  const [focusedMode, setFocusedMode] = useState(false);

  // Iter-53.1d: `?firm=` / `?phone=` re-brand the page at render time so
  // ops can pitch every buyer without cloning rows.
  // Iter-54.2c: on real client slugs (e.g. `vance-al-injury`) the
  // overrides are ONLY honored when a preview surface is active
  // (`?preview=1` admin, or `?demo=<token>` ops-minted preview link) —
  // that way public/organic traffic to `/lp/vance-al-injury` can never
  // be cosmetically spoofed by a firm=... in the URL, but every
  // Share-ALL preview link (including Vance) personalizes cleanly for
  // the prospective buyer being pitched. The `buyer_destination` on any
  // custom LP is server-side and never affected by client-side query
  // params, so this remains a purely visual layer.
  const isSampleSlug = (slug || "").startsWith("sample-");
  const allowBrandOverride = isSampleSlug || isPreviewSurface;
  const firmOverride = allowBrandOverride ? (params.get("firm") || "") : "";
  const phoneOverride = allowBrandOverride ? (params.get("phone") || "") : "";

  // Iter-52: shared anonymous session — starts the load-time timer that
  // covers the ENTIRE landing-page experience (Standard OR Quiz), so bot
  // defense sees the true elapsed time from page visit → submit.
  const session = useLandingSession({
    slug, isPreviewSurface,
  });

  // Iter-46: fetch per-slug SEO meta from the backend and inject it into
  // <head> — draft pages 404 that endpoint so admin previews leave the
  // tab title alone.
  useLandingSeo({ slug, preview: isPreviewSurface });

  useEffect(() => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (preview) qs.set("preview", "1");
    if (demoToken) qs.set("demo", demoToken);
    if (buyerParam) qs.set("b", buyerParam);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    let redirecting = false;
    axios
      .get(`${API}/public/custom-landing/${encodeURIComponent(slug)}${suffix}`)
      .then((r) => {
        // Buyer link to a funnel the buyer didn't pick → their first picked funnel.
        const forwardTo = r.data?.forward_to_funnel;
        if (forwardTo) {
          redirecting = true;
          navigate(`/lp/${forwardTo}${window.location.search}`, { replace: true });
          return;
        }
        // Legacy buyer URL → forward to the shared funnel with its buyer code.
        const target = r.data?.global_funnel_redirect;
        if (target && !r.data?.is_global_funnel) {
          redirecting = true;
          const next = new URLSearchParams(window.location.search);
          next.set("b", r.data.slug);
          navigate(`/lp/${target}?${next.toString()}`, { replace: true });
          return;
        }
        setPage(r.data);
      })
      .catch((e) => {
        const status = e.response?.status;
        if (status === 404) setError("This landing page isn't available.");
        else setError(e.response?.data?.detail || "Failed to load page");
      })
      .finally(() => { if (!redirecting) setLoading(false); });
  }, [slug, preview, demoToken, buyerParam, navigate]);

  // Shared funnel: which firm to show — from the visitor's location as the
  // page opens, then from the state (and answers) they pick in the form.
  const { liveBuyer, onAnswers } = useLiveBuyer({
    page, slug, affParam, sessionId: session?.sessionId, useLocation: !isPreviewSurface,
  });

  // Iter-52: resolve variant once the page config is loaded. Only fires
  // when the page has a real slug (skip on 404) — sticky by localStorage
  // via useLandingSession(). Preview surfaces respect `?variant=` for
  // admins who want to walk both sides of an A/B test.
  useEffect(() => {
    if (!page?.slug) return;
    let cancelled = false;
    session.assignVariant({
      forceVariant: previewVariant || null,
      demoToken: demoToken || null,
    }).then((data) => {
      if (cancelled) return;
      setAssignment(data);
    });
    return () => { cancelled = true; };
  }, [page?.slug, previewVariant, demoToken, session]);

  // Iter-45: inject configured marketing pixels once the page config
  // loads. Skipped for any preview surface (admin preview OR demo token)
  // so we don't pollute ad platforms with fake PageView / Lead events.
  // Iter-54.1: also skipped for sample landing pages — never fire
  // marketing pixels on demo surfaces.
  useEffect(() => {
    if (!page) return;
    const nonce = pixelCheckNonce();
    if (nonce) {
      if (pixelCheckSent.current) return;
      pixelCheckSent.current = true;
      reportPixelCheck(nonce, "landing", page.slug, page.is_sample_page ? [] : [page.pixels, page.buyer_pixels]);
      return;
    }
    if (!page.pixels || isPreviewSurface || page.is_sample_page) return;
    injectAllPixels(page.pixels);
    if (page.buyer_pixels) injectAllPixels(page.buyer_pixels);
  }, [page, isPreviewSurface]);

  // Iter-63.1: prospect-share engagement — fire ONCE per page load when
  // the visitor arrived via a `?demo=<token>` share link. Never runs on
  // real live traffic, never runs on `?preview=1` admin previews without
  // a share token, never runs on `sample-*` pages. Sample pages remain
  // preview-only demonstration surfaces.
  useEffect(() => {
    if (!demoToken || !page?.slug) return;
    if (page.is_sample_page) return;
    const shareSlug = page.buyer?.code || page.slug;
    emitPreviewEngagement(demoToken, shareSlug, "page_view", {
      isSamplePage: page.is_sample_page,
      dedupe: `${demoToken}:${shareSlug}:page_view`,
    });
  }, [demoToken, page?.slug, page?.buyer?.code, page?.is_sample_page]);

  if (loading) {
    return (
      <LandingLayout preview={isPreviewSurface} brand={brand} hidePhone>
        <div className="lp-section">
          <div className="label-mono">Loading…</div>
        </div>
      </LandingLayout>
    );
  }

  if (error || !page) {
    return (
      <LandingLayout preview={isPreviewSurface} brand={brand} hidePhone>
        <div className="lp-section" data-testid="custom-lp-error">
          <h1 className="lp-headline">Page not found</h1>
          <p className="lp-subhead">{error || "This landing page is not currently available."}</p>
        </div>
      </LandingLayout>
    );
  }

  // Iter-53.1d + Iter-54.2c: apply per-request overrides. Only used for
  // `sample-*` pages (any surface) OR on any slug behind a preview
  // surface (`?preview=1` admin or ops-minted `demo=<token>`). On every
  // other slug the overrides remain empty strings so the underlying
  // page data always wins.
  const activeBuyer = liveBuyer === undefined ? (page.buyer || null) : liveBuyer;
  const { shown, usingLink } = shownDetails(page, activeBuyer);
  // The buyer actually shown decides (live match included); otherwise the page's own setting.
  const hideLanderPhone = Boolean(page.phone_hidden_global) || (activeBuyer?.phone_display
    ? hidesPhone(activeBuyer.phone_display, "landers") : Boolean(page.effective_hide_phone));
  const displayFirmName = firmOverride || shown.firm_name;
  const displayPhone = phoneOverride || shown.phone;

  // Build the vertical descriptor LandingForm expects.
  // Iter-46: only the "other" (all-in-one PI) vertical shows the case-type
  // dropdown. Auto/truck/rideshare/motorcycle-scoped pages know exactly
  // what type they are, so the dropdown would be redundant noise.
  const showCaseTypeDropdown = (page.vertical || "other") === "other";
  const vertical = {
    slug: page.slug,
    accidentType: page.vertical || "other",
    kicker: page.hero_kicker,
    headline: page.hero_headline,
    headlineAccent: page.hero_headline_accent,
    subheadline: page.hero_subheadline,
    heroImage: page.hero_image_url,
    urgencyBadge: page.urgency_badge,
    stat1: page.stats?.[0] || { number: "24hr", label: "Attorney response guaranteed" },
    stat2: page.stats?.[1] || { number: "No fee", label: "Unless we recover for you" },
    stat3: page.stats?.[2] || { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When did the incident happen?",
    incidentLabel: "Date of Incident",
    caseDescriptionLabel: "Briefly describe what happened",
    ctaText: page.cta_text || "Get My Free Case Review",
    caseTypeSelector: showCaseTypeDropdown,
    // Iter-43 branding — carry firm/phone/TCPA through to LandingForm +
    // ThankYou so the buyer-branded page never loses identity.
    // Iter-53.1d + Iter-54.2c — honour URL-param overrides on `sample-*`
    // slugs (any surface) and on any slug behind a preview surface.
    firmName: displayFirmName,
    firmPhone: displayPhone,
    tcpaLanguage: shown.tcpa_language,
    // Iter-44: pass slug so LandingForm redirects ThankYou with slug=<slug>
    // — ThankYou then fetches the admin-authored thank-you copy for THIS
    // firm. On global funnels this is the buyer code (share-token scope).
    customSlug: activeBuyer?.code || page.slug,
    funnelSlug: (page.is_global_funnel || page.buyer_lander) ? page.slug : null,
    buyerLander: Boolean(page.buyer_lander),
    buyerCode: activeBuyer?.code || null,
    buyerSource: usingLink ? "link" : (activeBuyer ? "match" : null),
    onAnswers,
    // Iter-54.1: sample-mode flag propagates down through the form so the
    // submit path can short-circuit TrustedForm reads + client-side
    // pixel firing even before it hits the (already-short-circuiting)
    // backend endpoint. Belt-and-braces: the backend is the source of
    // truth, but the frontend also refuses to touch TrustedForm.
    isSamplePage: Boolean(page.is_sample_page),
  };

  // Apply the admin's brand color to BOTH --lp-accent (form buttons,
  // hero underline, phone bar accent) AND --lp-brand (form title, links).
  const brandStyle = shown.brand_color
    ? {
        "--lp-accent": shown.brand_color,
        "--lp-accent-dark": shown.brand_color,
        "--lp-brand": shown.brand_color,
      }
    : {};

  const layoutProps = {
    preview: isPreviewSurface,
    brand: brand || displayFirmName,
    hidePhone: hideLanderPhone,
    isSamplePage: Boolean(page.is_sample_page),
    // Iter-74.2 — one consistent buyer phone + policy URLs in the layout.
    brandPhone: displayPhone,
    privacyUrl: shown.privacy_url || null,
    termsUrl: shown.terms_url || null,
    brandLogo: shown.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${shown.logo_url}` : null,
    buyerCode: activeBuyer?.code || ((page.is_global_funnel || page.buyer_lander) ? null : page.slug),
  };
  const isCaseQuiz = page.form_experience === "case_quiz";
  // Iter-56: approved custom-tracking snippets (no-op on preview / demo / sample).
  const trackingEl = (
    <CustomTrackingLoader
      slug={page.slug}
      isPreviewSurface={isPreviewSurface || Boolean(pixelCheckNonce())}
      isSample={Boolean(page.is_sample_page)}
      surface="landing"
      context={{
        landing_page_slug: page.slug,
        landing_page_id: page.id,
        form_experience: isCaseQuiz ? "case_quiz" : (page.experiment_variant && page.experiment_variant !== "standard") ? "quiz" : (page.form_experience === "focused_funnel" ? "focused_funnel" : "standard"),
        experiment_variant: assignment?.variant || null,
        sub_id: params.get("sub") || params.get("sub_id") || params.get("sub_1") || null,
        utm_source: params.get("utm_source") || null,
        utm_campaign: params.get("utm_campaign") || null,
        utm_medium: params.get("utm_medium") || null,
        session_id: session?.sessionId || null,
      }}
    />
  );

  if (isCaseQuiz) {
    return (
      <LandingLayout {...layoutProps}>
        {trackingEl}
        <CaseFunnelView
          page={page}
          vertical={{ ...vertical, vertical: page.vertical || "other" }}
          preview={preview}
          demo={demoToken || null}
          session={session}
          firmName={activeBuyer ? displayFirmName : null}
          buyerCode={layoutProps.buyerCode}
        />
      </LandingLayout>
    );
  }

  const quizCfg = assignment?.quiz_config || page.quiz_config || {};
  const isQuizV2 = quizCfg.layout === "v2"
    && (assignment ? assignment.variant === "quiz" : page.form_experience === "quiz");
  if (isQuizV2) {
    return (
      <QuizV2Page
        page={page}
        vertical={vertical}
        layoutProps={layoutProps}
        brandStyle={brandStyle}
        trackingEl={trackingEl}
        phone={displayPhone}
        quizProps={{
          vertical: { ...vertical, vertical: page.vertical || "other" },
          preview,
          demo: demoToken || null,
          quizConfig: quizCfg,
          progressStyle: assignment?.quiz_progress_style || page.quiz_progress_style || "steps",
          session,
          experimentId: assignment?.experiment_id || null,
          variant: assignment?.variant || "quiz",
        }}
      />
    );
  }

  return (
    <LandingLayout {...layoutProps}>
      {trackingEl}
      <div style={brandStyle} data-testid={`custom-lp-${page.slug}`}>
        {/* Iter-54.1: DEMONSTRATION-PAGE notice — kept visible but
            visually restrained. Deliberately not a dominant ribbon so
            the sales pitch (what the firm's buyers actually see) still
            reads cleanly. */}
        {page.is_sample_page && (
          <div
            data-testid="custom-lp-sample-banner"
            role="status"
            aria-live="polite"
            style={{
              background: "#fef3c7",
              color: "#78350f",
              padding: "8px 16px",
              textAlign: "center",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderBottom: "1px solid #b45309",
              fontWeight: 700,
            }}
          >
            <span aria-hidden="true">◆</span>{" "}
            Demonstration page — no information submitted here will be sent to a law firm.
          </div>
        )}
        {/* Status ribbon for drafts / paused — admin preview ONLY, not
            shown on client-share demo links so buyers see a clean page. */}
        {preview && !demoToken && page.status !== "published" && (
          <div
            className="lp-preview-ribbon"
            style={{
              position: "sticky", top: 0, zIndex: 9,
              background: "#fbbf24", color: "#111",
              padding: "6px 16px",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, textTransform: "uppercase",
              letterSpacing: "0.15em", textAlign: "center",
              borderBottom: "1px solid #000", fontWeight: 700,
            }}
            data-testid="custom-lp-preview-ribbon"
          >
            Preview · Status: {page.status?.toUpperCase()}
          </div>
        )}

        {/* Iter-50: subtle client-preview banner so the buyer knows this
            is a private preview link and not a live prospect page. Kept
            visually quiet so it doesn't derail the sales conversation. */}
        {demoToken && (
          <div
            data-testid="custom-lp-demo-banner"
            style={{
              position: "sticky", top: 0, zIndex: 9,
              background: "#0f172a", color: "#f8fafc",
              padding: "8px 16px",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, letterSpacing: "0.12em",
              textAlign: "center", textTransform: "uppercase",
              borderBottom: "1px solid #000", fontWeight: 700,
            }}
          >
            Private preview · form submissions are disabled
          </div>
        )}

        {/* Sticky phone bar (mirrors lawsuit-winning pattern) — Iter-53:
            respects the page-level `hide_phone_display` toggle plus the
            platform-wide master. Backend merges both into
            `effective_hide_phone` for a single source of truth. */}
        {displayPhone && !hideLanderPhone && (
          <a
            href={`tel:${displayPhone.replace(/[^0-9+]/g, "")}`}
            data-testid="custom-lp-phone-bar"
            data-lp-cta="phone"
            data-lp-buyer={layoutProps.buyerCode || undefined}
            onClick={() => {
              // Iter-63.1: prospect-share phone-tap telemetry.
              if (demoToken && page?.slug && !page.is_sample_page) {
                emitPreviewEngagement(demoToken, page.buyer?.code || page.slug, "cta_phone_click", {
                  isSamplePage: page.is_sample_page,
                });
              }
            }}
            style={{
              display: "block",
              background: "#000",
              color: "#fff",
              padding: "10px 16px",
              textAlign: "center",
              textDecoration: "none",
              fontFamily: "'Chivo', system-ui, sans-serif",
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontSize: 13,
            }}
          >
            Free Case Evaluation — {displayPhone}
          </a>
        )}

        <section className="lp-hero" data-testid={`lp-hero-${page.slug}`}>
          <div
            className="lp-hero-bg"
            style={{ backgroundImage: page.hero_image_url ? `url(${page.hero_image_url})` : undefined }}
            aria-hidden="true"
          />
          <div className="lp-hero-overlay" aria-hidden="true" />
          <div className="lp-hero-inner">
            <div className="lp-hero-copy">
              {vertical.urgencyBadge && (
                <span className="lp-kicker">{vertical.urgencyBadge}</span>
              )}
              <h1 className="lp-headline" data-testid={`lp-headline-${page.slug}`}>
                {vertical.headline}
              </h1>
              {vertical.headlineAccent && (
                <div className="lp-headline-accent">{vertical.headlineAccent}</div>
              )}
              {vertical.subheadline && (
                <p className="lp-subhead">{vertical.subheadline}</p>
              )}
              {(page.stats || []).length > 0 && (
                <div className="lp-hero-stats">
                  {(page.stats || []).slice(0, 3).map((s, i) => (
                    <div className="lp-stat" key={i} style={{ animationDelay: `${0.15 * (i + 1)}s` }}>
                      <div className="lp-stat-num">{s.number}</div>
                      <div className="lp-stat-label">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              {(assignment?.variant === "focused_funnel"
                || (page.form_experience === "focused_funnel"
                    && (!assignment || assignment?.variant === "focused_funnel"))) ? (
                <FocusedFunnelForm
                  vertical={{ ...vertical, vertical: page.vertical || "other" }}
                  page={page}
                  preview={preview}
                  demo={demoToken || null}
                  focusedFunnelConfig={
                    assignment?.focused_funnel_config
                    || page.focused_funnel_config
                    || {}
                  }
                  session={session}
                  experimentId={assignment?.experiment_id || null}
                  variant={assignment?.variant || "focused_funnel"}
                  onFocusedModeChange={setFocusedMode}
                />
              ) : (assignment?.variant === "quiz") ? (
                <QuizForm
                  vertical={{ ...vertical, vertical: page.vertical || "other" }}
                  preview={preview}
                  demo={demoToken || null}
                  quizConfig={assignment?.quiz_config || page.quiz_config || {}}
                  progressStyle={assignment?.quiz_progress_style
                                  || page.quiz_progress_style || "steps"}
                  session={session}
                  experimentId={assignment?.experiment_id || null}
                  variant={assignment?.variant || "quiz"}
                />
              ) : (
                <LandingForm vertical={vertical} preview={preview} demo={demoToken || null} />
              )}
            </div>
          </div>
        </section>

        {/* Trust bar (shared) — Iter-57: hidden after Q1 when the visitor
            is inside the Focused Funnel so the qualification screen stays
            distraction-free. Standard + Quiz always render it. */}
        {!focusedMode && <SharedTrustBar />}

        {/* Custom content sections (admin-authored) — hidden in Focused mode. */}
        {!focusedMode && (page.content_sections || []).length > 0 && (
          <section className="lp-section" aria-label="Details">
            {(page.content_sections || []).map((c, i) => (
              <div key={i} style={{ marginBottom: 40 }} data-testid={`custom-lp-section-${i}`}>
                <div className="lp-eyebrow">Section {String(i + 1).padStart(2, "0")}</div>
                <h2 className="lp-section-title">{c.title}</h2>
                <p className="lp-section-sub" style={{ whiteSpace: "pre-line" }}>
                  {c.body}
                </p>
              </div>
            ))}
          </section>
        )}

        {/* Buyer's own approved testimonials — hidden when none, and in Focused Funnel mode. */}
        {!focusedMode && <ClassicTestimonials code={layoutProps.buyerCode} />}

        {/* Recent case results (shared) — hidden in Focused Funnel mode. */}
        {!focusedMode && <SharedCaseResults testId="custom-lp-case-results" />}

        {/* In the news (admin-authored links) — hidden in Focused Funnel mode. */}
        {!focusedMode && (page.in_the_news || []).length > 0 && (
          <section className="lp-section" aria-label="In the news">
            <div className="lp-eyebrow">In the news</div>
            <h2 className="lp-section-title">Coverage & references</h2>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {(page.in_the_news || []).map((n, i) => (
                <li key={i} style={{ marginBottom: 10 }}>
                  <a href={n.url} target="_blank" rel="noreferrer noopener"
                     data-testid={`custom-lp-news-${i}`}
                     style={{ color: "#000", textDecoration: "underline",
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 13, wordBreak: "break-all" }}>
                    {n.title || n.url}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Final CTA — hidden in Focused Funnel mode so the qualification
            flow can't compete with a marketing button. */}
        {!focusedMode && <ClassicFinalCta firm={displayFirmName} />}
      </div>
    </LandingLayout>
  );
}

function ClassicFinalCta({ firm }) {
  const cta = useFinalCta(firm, {
    title: "Your case review is free. Your time is valuable. Let's get started.",
    sub: `Talk to ${firm || "an experienced attorney"} — no obligation, no fee unless we win.`,
    button: "Start My Free Review",
  });
  return (
        <section className="lp-final-cta" data-testid="custom-lp-final-cta-section">
          <div className="lp-final-cta-inner">
            <div>
              <div className="lp-eyebrow lp-eyebrow-inverse">Ready when you are</div>
              <h2 className="lp-final-cta-title">{cta.title}</h2>
              <p className="lp-final-cta-sub">{cta.sub}</p>
            </div>
            <a
              href="#top"
              className="lp-cta lp-cta-inline"
              data-testid="custom-lp-final-cta"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              {cta.button} ↑
            </a>
          </div>
        </section>
  );
}


